import type { ViewRecord } from "@/shared/domain/views";

/**
 * The two lists behind the picker's Personal and Team tabs, plus `unknown` for
 * Views whose visibility was not reported. Kept separate so the caller folds it
 * in visibly rather than through a `??` buried here.
 */
export interface PartitionedViews {
  personal: ViewRecord[];
  team: ViewRecord[];
  unknown: ViewRecord[];
}

export interface PartitionOptions {
  /**
   * Keep private Views authored by someone else. Off, they are dropped: an org
   * owner receives every private View in the org through the read-everything
   * override, and listing them would fill the owner's Team tab with drafts
   * nobody chose to share. On for Roboto system users, who need to see what a
   * customer sees when supporting them.
   */
  includeColleaguesPrivate: boolean;
}

/**
 * Split Views into mine and everyone else's.
 *
 * "Personal" means private *and* authored by the caller. Visibility alone is
 * not enough: an org owner or Roboto sysadmin can read every View in the org,
 * private ones included, so they receive colleagues' private Views too. Those
 * are dropped unless `includeColleaguesPrivate` keeps them, in which case they
 * go under Team, where the row names their author, rather than being presented
 * as the caller's own. A plain member never receives another person's private
 * View, so for them the two readings agree.
 *
 * An absent visibility is deliberately not treated as private: that would show
 * a shared View under Personal. Callers fold `unknown` into Team, the safer
 * reading. Input order is preserved within each list.
 */
export function partitionViewsByVisibility(
  views: readonly ViewRecord[],
  currentUserId: string | undefined,
  { includeColleaguesPrivate }: PartitionOptions,
): PartitionedViews {
  const partitioned: PartitionedViews = {
    personal: [],
    team: [],
    unknown: [],
  };

  for (const view of views) {
    bucketFor(view, currentUserId, includeColleaguesPrivate, partitioned)?.push(
      view,
    );
  }

  return partitioned;
}

/** The list a View belongs in, or `null` to leave it out. */
function bucketFor(
  view: ViewRecord,
  currentUserId: string | undefined,
  includeColleaguesPrivate: boolean,
  partitioned: PartitionedViews,
): ViewRecord[] | null {
  switch (view.visibility) {
    case "private":
      if (view.created_by === currentUserId) {
        return partitioned.personal;
      }
      return includeColleaguesPrivate ? partitioned.team : null;
    case "organization":
      return partitioned.team;
    default:
      return partitioned.unknown;
  }
}
